import { z } from 'zod';
import type { BoardData, CatalogEntry } from '../model/schema.js';
import {
  BoardData as BoardDataSchema,
  CatalogEntry as CatalogEntrySchema,
} from '../model/schema.js';
import { migrate } from '../doc/migrate.js';
import { BOARD_EXPORT_FORMAT } from './export.js';

/**
 * Portable board file (decision U7, `.stageplottter`): the board document plus every referenced
 * file (images, PDFs) embedded as base64 and the workspace catalog entries the items use. Moves a
 * board to another instance; history, comments, open items, links and discussion layers are
 * deliberately not part of it.
 */
export const PORTABLE_BOARD_FORMAT = 'stageplottter.board.v2';
export const PORTABLE_BOARD_EXTENSION = '.stageplottter';

/** File types a board may store (uploads and embedded files share this whitelist). */
export const BOARD_FILE_MIMES = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
  'application/pdf',
] as const;
export const BoardFileMime = z.enum(BOARD_FILE_MIMES);
export type BoardFileMime = z.infer<typeof BoardFileMime>;

/** Defaults for {@link readPortableImport}: 10 MB per file (R13/R14), 100 MB per package. */
export const PORTABLE_MAX_FILE_BYTES = 10 * 1024 * 1024;
export const PORTABLE_MAX_TOTAL_BYTES = 100 * 1024 * 1024;

const BASE64 = /^[A-Za-z0-9+/]*={0,2}$/;

export const PortableFile = z.object({
  name: z.string().min(1).max(255),
  mime: BoardFileMime,
  /** Decoded size in bytes (checked against the payload on import). */
  size: z.number().int().nonnegative(),
  dataBase64: z.string().regex(BASE64, 'Invalid base64'),
});
export type PortableFile = z.infer<typeof PortableFile>;

const FileId = z.string().min(1).max(64);

/** Envelope as written by {@link buildPortableExport} (and the REST export endpoint). */
export const PortableBoard = z.object({
  format: z.literal(PORTABLE_BOARD_FORMAT),
  exportedAt: z.string().datetime({ offset: true }),
  data: BoardDataSchema,
  files: z.record(FileId, PortableFile),
  catalogEntries: z.array(CatalogEntrySchema),
});
export type PortableBoard = z.infer<typeof PortableBoard>;

/** Read side: `data` may be an older schema version (migrated afterwards). */
const PortableEnvelope = PortableBoard.extend({
  data: z.unknown(),
  files: z.record(FileId, PortableFile).default({}),
  catalogEntries: z.array(CatalogEntrySchema).default([]),
});

export type PortableImportError =
  'invalid-json' | 'wrong-format' | 'invalid-data' | 'invalid-files' | 'too-large';

export type PortableImportResult =
  | {
      ok: true;
      /** `v1` = plain JSON export (R12) without files. */
      format: 'v1' | 'v2';
      data: BoardData;
      /** Only the files the board references. */
      files: Record<string, PortableFile>;
      /** Unique by `ref` (first wins). */
      catalogEntries: CatalogEntry[];
    }
  | { ok: false; error: PortableImportError; message: string };

export interface PortableLimits {
  maxFileBytes?: number;
  maxTotalBytes?: number;
}

/** Every file id the board references: free-form images and the PDFs they were rendered from. */
export function referencedFileIds(data: Pick<BoardData, 'freeform'>): string[] {
  const ids = new Set<string>();
  for (const el of Object.values(data.freeform)) {
    if (el.fileId) ids.add(el.fileId);
    if (el.sourceFileId) ids.add(el.sourceFileId);
  }
  return [...ids].sort();
}

/** Workspace catalog entries referenced by the board's items (`catalogRef`). */
export function usedCatalogEntries(
  data: Pick<BoardData, 'items'>,
  entries: readonly CatalogEntry[],
): CatalogEntry[] {
  const refs = new Set(Object.values(data.items).map((i) => i.catalogRef));
  return entries.filter((e) => refs.has(e.ref));
}

/**
 * Adds the `incoming` entries whose ref the workspace catalog does not have yet; existing
 * entries are never overwritten. Returns the merged list and the added refs.
 */
export function mergeCatalogEntries(
  existing: readonly CatalogEntry[],
  incoming: readonly CatalogEntry[],
): { entries: CatalogEntry[]; added: string[] } {
  const refs = new Set(existing.map((e) => e.ref));
  const entries = [...existing];
  const added: string[] = [];
  for (const e of incoming) {
    if (refs.has(e.ref)) continue;
    refs.add(e.ref);
    entries.push(e);
    added.push(e.ref);
  }
  return { entries, added };
}

/** Rewrites file references (`fileId`, `sourceFileId`); ids without a mapping stay as they are. */
export function remapFileIds(
  data: BoardData,
  mapping: Readonly<Record<string, string>>,
): BoardData {
  const map = (id: string): string => (Object.hasOwn(mapping, id) ? mapping[id]! : id);
  const freeform: BoardData['freeform'] = {};
  for (const [key, el] of Object.entries(data.freeform)) {
    const next = { ...el };
    if (el.fileId !== undefined) next.fileId = map(el.fileId);
    if (el.sourceFileId !== undefined) next.sourceFileId = map(el.sourceFileId);
    freeform[key] = next;
  }
  return { ...data, freeform };
}

/** Decoded byte length of a padded base64 string. */
export function base64DecodedSize(b64: string): number {
  const pad = b64.endsWith('==') ? 2 : b64.endsWith('=') ? 1 : 0;
  return (b64.length / 4) * 3 - pad;
}

/**
 * Builds the v2 package. `files` may contain more than needed: only referenced files are
 * embedded; referenced files missing from `files` are left out (the reference then dangles on
 * import, exactly as it does on the source board). Catalog entries are filtered to the used ones.
 */
export function buildPortableExport(
  data: BoardData,
  files: Readonly<Record<string, PortableFile>>,
  catalogEntries: readonly CatalogEntry[],
  now: Date = new Date(),
): PortableBoard {
  const valid = BoardDataSchema.parse(data);
  const embedded: Record<string, PortableFile> = {};
  for (const id of referencedFileIds(valid)) {
    const f = files[id];
    if (f) embedded[id] = PortableFile.parse(f);
  }
  return {
    format: PORTABLE_BOARD_FORMAT,
    exportedAt: now.toISOString(),
    data: valid,
    files: embedded,
    catalogEntries: usedCatalogEntries(valid, catalogEntries).map((e) =>
      CatalogEntrySchema.parse(e),
    ),
  };
}

const fail = (error: PortableImportError, message: string): PortableImportResult => ({
  ok: false,
  error,
  message,
});

function migrateData(data: unknown): BoardData | string {
  try {
    return migrate(data);
  } catch (e) {
    return (e as Error).message;
  }
}

/**
 * Reads a portable board file (text or parsed JSON): accepts v2 packages and v1 JSON exports,
 * validates envelope, files (mime whitelist, base64, declared sizes, limits) and data (migrated to
 * the current schema). Never throws.
 */
export function readPortableImport(
  input: string | unknown,
  limits: PortableLimits = {},
): PortableImportResult {
  const maxFile = limits.maxFileBytes ?? PORTABLE_MAX_FILE_BYTES;
  const maxTotal = limits.maxTotalBytes ?? PORTABLE_MAX_TOTAL_BYTES;
  let parsed: unknown = input;
  if (typeof input === 'string') {
    // base64 inflates by 4/3; anything far beyond the limit is rejected before parsing
    if (input.length > Math.ceil(maxTotal * 1.4) + 1024 * 1024)
      return fail('too-large', `Board files are limited to ${maxTotal} bytes`);
    try {
      parsed = JSON.parse(input);
    } catch (e) {
      return fail('invalid-json', (e as Error).message);
    }
  }
  const format =
    typeof parsed === 'object' && parsed !== null
      ? (parsed as Record<string, unknown>)['format']
      : undefined;
  if (format === BOARD_EXPORT_FORMAT) {
    const data = migrateData((parsed as Record<string, unknown>)['data']);
    if (typeof data === 'string') return fail('invalid-data', data);
    return { ok: true, format: 'v1', data, files: {}, catalogEntries: [] };
  }
  if (format !== PORTABLE_BOARD_FORMAT)
    return fail(
      'wrong-format',
      `Expected format ${PORTABLE_BOARD_FORMAT} or ${BOARD_EXPORT_FORMAT}`,
    );
  const envelope = PortableEnvelope.safeParse(parsed);
  if (!envelope.success) {
    const issue = envelope.error.issues[0]!;
    const code = issue.path[0] === 'files' ? 'invalid-files' : 'invalid-data';
    return fail(code, `${issue.path.join('.')}: ${issue.message}`);
  }
  const data = migrateData(envelope.data.data);
  if (typeof data === 'string') return fail('invalid-data', data);

  const files: Record<string, PortableFile> = {};
  let total = 0;
  for (const id of referencedFileIds(data)) {
    const f = envelope.data.files[id];
    if (!f) continue;
    if (f.dataBase64.length % 4 !== 0 || base64DecodedSize(f.dataBase64) !== f.size)
      return fail('invalid-files', `File ${id}: payload does not match its size`);
    if (f.size > maxFile)
      return fail('too-large', `File ${id} (${f.name}) exceeds ${maxFile} bytes`);
    total += f.size;
    if (total > maxTotal) return fail('too-large', `Embedded files exceed ${maxTotal} bytes`);
    files[id] = f;
  }
  const seen = new Set<string>();
  const catalogEntries = envelope.data.catalogEntries.filter((e) => {
    if (seen.has(e.ref)) return false;
    seen.add(e.ref);
    return true;
  });
  return { ok: true, format: 'v2', data, files, catalogEntries };
}
